import React from 'react';
import { CartStatus } from '@/features/cart/types';

interface CartStatusBannerProps {
  status: CartStatus;
  onCancelCheckout?: () => void;
  isCanceling?: boolean;
  isExpired?: boolean;
}

const CartStatusBanner: React.FC<CartStatusBannerProps> = ({
  status,
  isExpired = false,
}) => {
  // Show expired session message when timer has expired
  if (isExpired && (status === CartStatus.PendingPayment || status === CartStatus.FailedPayment)) {
    return (
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-orange-400 font-semibold text-sm mb-1">Payment session ended</h3>
            <p className="text-gray-300 text-sm">Your payment session has expired. Please cancel and start a new checkout.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === CartStatus.PendingPayment) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-yellow-400 font-semibold text-sm mb-1">Payment Pending</h3>
            <p className="text-gray-300 text-sm">Your payment is pending. Please complete your payment to secure your booking.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === CartStatus.FailedPayment) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-red-400 font-semibold text-sm mb-1">Payment Failed</h3>
            <p className="text-gray-300 text-sm">Your payment failed. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === CartStatus.Completed || status === CartStatus.SuccessfulPayment) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-green-400 font-semibold text-sm mb-1">Payment Completed</h3>
            <p className="text-gray-300 text-sm">Your payment has been successfully processed. Scroll down to download your invoice.</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CartStatusBanner;

